import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import type { SimulatorQuoteData } from '../page';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CTAProps {
  simulatorQuote?: SimulatorQuoteData | null;
  onClearSimulatorQuote?: () => void;
}

export default function CTA({ simulatorQuote, onClearSimulatorQuote }: CTAProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    service: '전원주택 정원관리',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (simulatorQuote && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [simulatorQuote]);

  const getServiceImage = (service: string) => {
    if (service.includes('전원주택') || service.includes('정원') || service.includes('제초') || service.includes('계절') || service.includes('연간')) {
      return {
        url: 'https://public.readdy.ai/ai/img_res/34fb3fb409c6b9c9c758f58d83e9d017.jpg',
        label: '전원주택 정원 관리'
      };
    } else if (service.includes('벌초') || service.includes('산소') || service.includes('선산') || service.includes('석축') || service.includes('보수')) {
      return {
        url: 'https://readdy.ai/api/search-image?query=professional%20drone%20aerial%20photography%20bird%20eye%20view%20from%20directly%20above%20beautifully%20maintained%20Korean%20ancestral%20grave%20site%20with%20neatly%20trimmed%20short%20green%20grass%2C%20clean%20visible%20stone%20markers%20and%20burial%20mounds%2C%20well-groomed%20hillside%20cemetery%2C%20authentic%20drone%20camera%20perspective%20showing%20entire%20pristine%20cemetery%20area%20from%20top%20down%2C%20bright%20natural%20sunlight%2C%20after%20professional%20maintenance%2C%20realistic%20aerial%20shot&width=800&height=500&seq=cta-grave-preview-001&orientation=landscape',
        label: '산소·선산 관리'
      };
    }
    return null;
  };

  const currentServiceImage = getServiceImage(formData.service);

  const buildSimulatorSummary = (q: SimulatorQuoteData): string => {
    const lines: string[] = [];
    lines.push('=== 견적 시뮬레이터 선택 내역 ===');
    lines.push(`기본 관리비: ${q.graveLabel} — ${q.graveCount.toLocaleString()}원`);
    lines.push(`예상 면적: ${q.areaSize}평${q.areaExtra > 0 ? ` (면적 추가비 +${q.areaExtra.toLocaleString()}원)` : ''}`);
    if (q.coreItems.length > 0) {
      lines.push('[현장 난이도]');
      q.coreItems.forEach(i => lines.push(`  · ${i.title}: +${i.price.toLocaleString()}원`));
    }
    if (q.extraItems.length > 0) {
      lines.push('[추가 서비스]');
      q.extraItems.forEach(i => lines.push(`  · ${i.title}: +${i.price.toLocaleString()}원`));
    }
    lines.push(`예상 합계: ${q.total.toLocaleString()}원~`);
    return lines.join('\n');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const simulatorSummary = simulatorQuote ? buildSimulatorSummary(simulatorQuote) : '';
      const fullMessage = simulatorSummary
        ? `${simulatorSummary}\n\n[추가 요청사항]\n${formData.message}`
        : formData.message;

      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, key === 'message' ? fullMessage : value);
      });
      if (simulatorQuote) {
        formBody.append('simulator_total', String(simulatorQuote.total));
        formBody.append('simulator_summary', buildSimulatorSummary(simulatorQuote));
      }

      const formResponse = await fetch('https://readdy.ai/api/form/d6pcdiea739gopmlgorg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      const { error: insertError } = await supabase
        .from('quote_requests')
        .insert({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          location: formData.location,
          service: formData.service,
          message: fullMessage,
          status: 'pending',
          ...(simulatorQuote ? {
            simulator_total: simulatorQuote.total,
            simulator_items: JSON.stringify({
              graveLabel: simulatorQuote.graveLabel,
              graveCount: simulatorQuote.graveCount,
              areaSize: simulatorQuote.areaSize,
              areaExtra: simulatorQuote.areaExtra,
              coreItems: simulatorQuote.coreItems,
              extraItems: simulatorQuote.extraItems,
              total: simulatorQuote.total,
            })
          } : {})
        });

      if (insertError) {
        console.error('Supabase insert error:', insertError);
      }

      if (formResponse.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', location: '', service: '전원주택 정원관리', message: '' });
        if (onClearSimulatorQuote) onClearSimulatorQuote();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section ref={sectionRef} id="contact" className="relative py-16 sm:py-28 px-4 sm:px-6 overflow-hidden" style={{background: 'linear-gradient(155deg, #111e3a 0%, #0d1f3c 50%, #152a52 100%)'}}>
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(112deg, transparent 43%, #c9a227 44%, #c9a227 44.4%, transparent 45%), linear-gradient(74deg, transparent 62%, #c9a227 63%, #c9a227 63.3%, transparent 64%)', backgroundSize: '370px 370px'}}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-[1.8] tracking-tight">
            지금 바로 무료 위성 분석<br />견적을 신청하세요
          </h2>
          <p className="text-sm sm:text-xl md:text-2xl text-white/60 font-light leading-relaxed">
            10년치 지형 데이터 분석부터 정밀 견적까지, 48시간 내 무료 제공
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-10 sm:mb-16">
          <form id="satellite-analysis-form" data-readdy-form onSubmit={handleSubmit} className="rounded-2xl sm:rounded-3xl p-7 sm:p-10 md:p-14 border border-[#c9a227]/20" style={{background: 'linear-gradient(160deg, rgba(30,58,110,0.7), rgba(13,31,60,0.85))'}}>

            {simulatorQuote && (
              <div className="mb-7 rounded-2xl border border-[#c9a227]/40 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5 bg-[#c9a227]/15">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calculator-line text-[#c9a227] text-base"></i>
                    </div>
                    <span className="text-[#c9a227] font-bold text-sm tracking-wide">견적 시뮬레이터 선택 내역</span>
                  </div>
                  <button
                    type="button"
                    onClick={onClearSimulatorQuote}
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition cursor-pointer"
                  >
                    <i className="ri-close-line text-white/50 text-sm"></i>
                  </button>
                </div>

                <div className="px-5 py-4 space-y-2 bg-white/5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/70">기본 관리비 ({simulatorQuote.graveLabel})</span>
                    <span className="text-white font-medium">{simulatorQuote.graveCount.toLocaleString()}원</span>
                  </div>
                  {simulatorQuote.areaExtra > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/70">면적 추가비 ({simulatorQuote.areaSize}평)</span>
                      <span className="text-white font-medium">+{simulatorQuote.areaExtra.toLocaleString()}원</span>
                    </div>
                  )}
                  {simulatorQuote.coreItems.length > 0 && (
                    <>
                      <div className="pt-1 pb-0.5">
                        <span className="text-[#c9a227]/80 text-xs font-semibold tracking-wide">현장 난이도</span>
                      </div>
                      {simulatorQuote.coreItems.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm pl-3">
                          <span className="text-white/60 flex items-center gap-1.5">
                            <i className="ri-checkbox-circle-fill text-[#c9a227]/70 text-xs"></i>
                            {item.title}
                          </span>
                          <span className="text-white/90 font-medium">+{item.price.toLocaleString()}원</span>
                        </div>
                      ))}
                    </>
                  )}
                  {simulatorQuote.extraItems.length > 0 && (
                    <>
                      <div className="pt-1 pb-0.5">
                        <span className="text-[#c9a227]/80 text-xs font-semibold tracking-wide">추가 서비스</span>
                      </div>
                      {simulatorQuote.extraItems.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm pl-3">
                          <span className="text-white/60 flex items-center gap-1.5">
                            <i className="ri-checkbox-circle-fill text-[#c9a227]/70 text-xs"></i>
                            {item.title}
                          </span>
                          <span className="text-white/90 font-medium">+{item.price.toLocaleString()}원</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="pt-3 mt-1 border-t border-[#c9a227]/20 flex items-center justify-between">
                    <span className="text-white font-bold text-sm">예상 합계</span>
                    <span className="text-[#c9a227] font-extrabold text-lg">{simulatorQuote.total.toLocaleString()}원~</span>
                  </div>
                  <p className="text-white/35 text-[10px] text-right">* 현장 실측 후 최종 확정 / 부가세 별도</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mb-5 sm:mb-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-white/75 mb-2 tracking-wide">이름 *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                  className="w-full px-4 py-3 rounded-xl border border-[#c9a227]/20 focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40 text-sm leading-relaxed text-white placeholder-white/30 bg-white/5"
                  placeholder="홍길동" />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-white/75 mb-2 tracking-wide">연락처 *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                  className="w-full px-4 py-3 rounded-xl border border-[#c9a227]/20 focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40 text-sm leading-relaxed text-white placeholder-white/30 bg-white/5"
                  placeholder="010-1234-5678" />
              </div>
            </div>
            <div className="mb-5 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-white/75 mb-2 tracking-wide">이메일 *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required
                className="w-full px-4 py-3 rounded-xl border border-[#c9a227]/20 focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40 text-sm leading-relaxed text-white placeholder-white/30 bg-white/5"
                placeholder="example@email.com" />
            </div>
            <div className="mb-5 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-white/75 mb-2 tracking-wide">위치 (주소 또는 지역) *</label>
              <input type="text" name="location" value={formData.location} onChange={handleChange} required
                className="w-full px-4 py-3 rounded-xl border border-[#c9a227]/20 focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40 text-sm leading-relaxed text-white placeholder-white/30 bg-white/5"
                placeholder="예: 진주시 금산면" />
            </div>
            <div className="mb-5 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-white/75 mb-2 tracking-wide">서비스 종류 *</label>
              <select name="service" value={formData.service} onChange={handleChange} required
                className="w-full px-4 py-3 rounded-xl border border-[#c9a227]/20 focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40 text-sm cursor-pointer leading-relaxed text-white bg-[#0d1f3c]">
                <optgroup label="전원주택 관리">
                  <option value="전원주택 정원관리">정원 관리</option>
                  <option value="전원주택 제초">제초 서비스</option>
                  <option value="전원주택 계절유지보수">계절별 유지보수</option>
                  <option value="전원주택 연간구독">연간 구독 패키지</option>
                </optgroup>
                <optgroup label="산소·선산 관리">
                  <option value="벌초 대행">벌초 대행</option>
                  <option value="묶음 벌초">묶음 벌초 할인</option>
                  <option value="석축/보수">전문 석축/보수</option>
                </optgroup>
                <option value="기타">기타</option>
              </select>
            </div>

            {currentServiceImage && (
              <div className="mb-5 sm:mb-6 relative rounded-xl sm:rounded-2xl overflow-hidden shadow-md border border-[#c9a227]/15" style={{ height: '180px' }}>
                <img src={currentServiceImage.url} alt={currentServiceImage.label} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/80 via-[#0d1f3c]/30 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-drone-line text-[#c9a227] text-sm"></i>
                    </div>
                    <span className="text-[#c9a227] text-xs font-medium tracking-wide">드론 촬영 예시</span>
                  </div>
                  <h4 className="text-white text-base sm:text-xl font-bold tracking-tight">{currentServiceImage.label}</h4>
                </div>
              </div>
            )}

            <div className="mb-5 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-white/75 mb-2 tracking-wide">추가 요청사항</label>
              <textarea name="message" value={formData.message} onChange={handleChange} maxLength={500} rows={4}
                className="w-full px-4 py-3 rounded-xl border border-[#c9a227]/20 focus:outline-none focus:ring-2 focus:ring-[#c9a227]/40 resize-none text-sm leading-[1.8] text-white placeholder-white/30 bg-white/5"
                placeholder="궁금하신 사항이나 특별한 요청사항을 입력해주세요 (최대 500자)" />
              <div className="text-right text-xs text-white/35 mt-1.5">{formData.message.length}/500</div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-5 sm:mb-6">
              <button type="submit" disabled={isSubmitting}
                className="flex-1 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap tracking-wide text-[#0d1f3c] hover:opacity-90 transition-opacity"
                style={{background: 'linear-gradient(135deg, #e8c84a, #b8860b)'}}>
                {isSubmitting ? '전송 중...' : '견적문의 및 상담 신청'}
              </button>
              <button type="button"
                onClick={() => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement; if (btn) btn.click(); }}
                className="flex-1 relative py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border border-[#c9a227]/30 bg-white/10 text-white group hover:scale-[1.02] active:scale-[0.98]">
                <span className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
                </span>
                <span className="relative flex items-center justify-center w-3 h-3">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-600"></span>
                </span>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-chat-smile-2-line text-xl"></i>
                </div>
                <span>챗봇 상담하기</span>
                <span className="absolute -top-2.5 -right-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md animate-bounce whitespace-nowrap">
                  즉시연결
                </span>
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-3 p-4 bg-[#c9a227]/10 border border-[#c9a227]/30 rounded-xl text-[#c9a227] text-center text-xs sm:text-sm leading-relaxed">
                신청이 완료되었습니다! 48시간 내에 연락드리겠습니다.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-center text-xs sm:text-sm leading-relaxed">
                전송 중 오류가 발생했습니다. 다시 시도해주세요.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
